import { html } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
  state,
} from 'lit/decorators.js';
import createPanZoom, { PanZoom, PanZoomOptions } from 'panzoom';
import {
  TmDialogElement,
  TmDialogElementStyle,
} from '@thiememeulenhoff/lit-sdk';
import { tagName } from '../../../helpers';
import { styles } from './image.styles';

const tag = tagName('image-modal');

@customElement(tag)
export class ImageModalComponent extends TmDialogElement {
  static override styles = [TmDialogElementStyle, styles];

  @property({ type: Number })
  maxZoom = 2;

  @property({ type: Number })
  minZoom = 1;

  @query('#full-size-image')
  image!: HTMLImageElement;

  @query('#zoom-out')
  zoomOutButton!: HTMLButtonElement;

  @query('#zoom-in')
  zoomInButton!: HTMLButtonElement;

  @queryAssignedElements({ selector: 'img' })
  slottedImages!: Array<HTMLElement>;

  @state()
  protected panZoom!: PanZoom;

  @state()
  protected panZoomOptions: PanZoomOptions = {
    minZoom: this.minZoom,
    maxZoom: this.maxZoom,
    smoothScroll: false,
    disableKeyboardInteraction: true,
    // This doesn't work as expected when set to `true`
    // See: https://github.com/anvaka/panzoom/issues/112
    bounds: true,
    boundsPadding: 0.01,
    zoomDoubleClickSpeed: 1, // Disable double click
  };

  constructor() {
    super();
    super.backdropClickEnabled = true;
  }

  override async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.panZoom) return;
    this.panZoom.dispose();
  }

  override onClose() {
    super.onClose();
    this.panZoom.dispose();
  }

  override extraControls() {
    return html`
      <div class="controls">
        <button
          id="zoom-out"
          class="control-button"
          @click=${this.#zoomOut}
          @touchstart=${this.#zoomOut}
          aria-label="Afbeelding verkleinen"
        >
          <a-icon name="zoom-out"></a-icon>
        </button>
        <button
          id="zoom-in"
          class="control-button"
          @click=${this.#zoomIn}
          @touchstart=${this.#zoomIn}
          aria-label="Afbeelding vergroten"
        >
          <a-icon name="zoom-in"></a-icon>
        </button>
      </div>
    `;
  }

  override open() {
    super.open();
    this.#panZoom();
  }

  #panZoom() {
    this.panZoom = createPanZoom(this.slottedImages[0], this.panZoomOptions);
    this.panZoom.on('transform', (event: PanZoom) => {
      const getTransform = event.getTransform();
      const disableZoomOut = this.panZoom.getMinZoom() === getTransform.scale;
      const disableZoomIn = this.panZoom.getMaxZoom() === getTransform.scale;
      this.zoomOutButton.disabled = disableZoomOut;
      this.zoomInButton.disabled = disableZoomIn;
    });
  }

  #zoomIn() {
    this.panZoom.smoothZoom(
      this.#getHorizontalCenter(),
      this.#getVerticalCenter(),
      1.5,
    );
  }

  #zoomOut() {
    this.panZoom.smoothZoom(
      this.#getHorizontalCenter(),
      this.#getVerticalCenter(),
      0.66,
    );
  }

  #getHorizontalCenter(): number {
    return window.innerWidth / 4;
  }

  #getVerticalCenter(): number {
    return window.innerHeight / 4;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-image-modal': ImageModalComponent;
  }
}
